<script setup lang="ts">
const props = withDefaults(defineProps<{
  title: string;
  description: string;
  buttonLabel?: string;
  emptyPreview?: string;
}>(), {
  buttonLabel: "Edit Markdown…",
  emptyPreview: "No Markdown content configured.",
});

const model = defineModel<string>({ default: "" });
const drawerOpen = shallowRef(false);
const isFullscreen = shallowRef(false);
const draft = shallowRef("");

const monacoOptions = {
  automaticLayout: true,
  wordWrap: "on",
  minimap: { enabled: false },
};

const drawerWidthClass = computed(() =>
  isFullscreen.value
    ? "data-[vaul-drawer-direction=right]:w-screen data-[vaul-drawer-direction=right]:sm:max-w-none"
    : "data-[vaul-drawer-direction=right]:w-[50vw] data-[vaul-drawer-direction=right]:sm:max-w-[800px]",
);
const preview = computed(() => model.value.trim() || props.emptyPreview);

function openEditor() {
  draft.value = model.value;
  isFullscreen.value = false;
  drawerOpen.value = true;
}

function apply() {
  model.value = draft.value;
  drawerOpen.value = false;
}
</script>

<template>
  <div class="space-y-2">
    <p class="max-h-16 overflow-hidden whitespace-pre-wrap text-[11.5px] text-muted-foreground">
      {{ preview }}
    </p>

    <Button
      variant="outline"
      size="sm"
      class="w-full justify-start rounded-sm"
      @click="openEditor"
    >
      <Icon mode="svg" size="15" name="lucide:file-pen-line" class="mr-2 text-muted-foreground" />
      {{ buttonLabel }}
    </Button>

    <Drawer v-model:open="drawerOpen" direction="right">
      <DrawerContent
        :class="['flex h-full flex-col transition-[width] duration-200', drawerWidthClass]"
      >
        <DrawerHeader class="flex flex-row items-start justify-between">
          <div>
            <DrawerTitle>{{ title }}</DrawerTitle>
            <DrawerDescription>{{ description }}</DrawerDescription>
          </div>
          <Button
            variant="ghost"
            size="icon"
            class="size-8 shrink-0"
            @click="isFullscreen = !isFullscreen"
          >
            <Icon
              mode="svg"
              size="16"
              :name="isFullscreen ? 'lucide:minimize-2' : 'lucide:maximize-2'"
            />
          </Button>
        </DrawerHeader>

        <div class="flex-1 overflow-hidden px-4">
          <ClientOnly>
            <MonacoEditor
              v-model="draft"
              lang="markdown"
              :options="monacoOptions"
              class="h-full w-full rounded-sm border"
            >
              Loading...
            </MonacoEditor>
          </ClientOnly>
        </div>

        <DrawerFooter>
          <Button @click="apply">Apply</Button>
          <DrawerClose as-child>
            <Button variant="outline">Cancel</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  </div>
</template>
